/* @jsx jsx */
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { PlayerWithPlugins } from 'video.js';

import type { PossibleResolution, ResolutionOption } from 'bundles/video-player/types/VideoPlayer';
import {
  enableAutoResolutionSwitching,
  getInitialResolution,
  saveResolutionInUserPreferences,
  trackVisualHlsResolutionChange,
} from 'bundles/video-player/utils/resolutionUtils';
import userPreferences from 'bundles/video-player/utils/userPreferences';
import { getDefaultVideoResolution, loadNewSrc } from 'bundles/video-player/utils/videoUtils';

export type UseVideoQualityControlsPayload = {
  player: PlayerWithPlugins;
  enableHls?: boolean;
  hlsSource: string | undefined;
};

export const useVideoQualityControls = ({ player, enableHls, hlsSource }: UseVideoQualityControlsPayload) => {
  const initialResolutionOption = useMemo(
    () =>
      getInitialResolution({
        enableHlsExperiment: !!enableHls,
        isAutoAllowed: true,
      }),
    [enableHls]
  ) as ResolutionOption;

  const supportedResolutions = player.getSupportedResolutions();

  // We check for a supported resolution in case the saved resolution is available for one video but not subsequent ones
  const [currentResolution, setCurrentResolution] = useState<PossibleResolution | ''>(
    initialResolutionOption === 'auto' ? '' : initialResolutionOption
  );
  const [currentResolutionOption, setCurrentResolutionOption] = useState<ResolutionOption>(initialResolutionOption);

  const onResolutionOptionClick = useCallback<(resolutionOption: ResolutionOption) => void>(
    (resolutionOption) => {
      if (resolutionOption === 'auto') {
        userPreferences.set('hlsResolution', 'auto');

        // @ts-expect-error - this is valid syntax; not sure why the types don't align
        if (player.currentSource().type !== 'application/x-mpegURL' && enableHls && hlsSource) {
          loadNewSrc({
            player,
            src: hlsSource,
            type: 'application/x-mpegURL',
          });
        }

        enableAutoResolutionSwitching({
          player,
          onAutoResolutionEnabled: () => {
            setCurrentResolutionOption(resolutionOption);
          },
        });
      } else {
        saveResolutionInUserPreferences(resolutionOption);
        // Sometimes the player is playing an HLS source, but the player.resolution api has the same mp4 resolution stored that we want to change to;
        // therefore, we need to force the resolution change to the correct one.
        player.resolution(resolutionOption, { forceResolutionChange: true });
        setCurrentResolutionOption(resolutionOption);
      }
    },
    [enableHls, hlsSource, player]
  );

  useEffect(() => {
    if (enableHls) {
      trackVisualHlsResolutionChange({ player, setCurrentResolution });
    }
  }, [enableHls, player]);

  useEffect(() => {
    const onResolutionChange = () => {
      const currResolution = player ? player.resolution() : getDefaultVideoResolution();
      setCurrentResolution(currResolution);
      setCurrentResolutionOption(currResolution);
    };

    player.on('resolutionchange', onResolutionChange);

    return () => {
      player.off('resolutionchange', onResolutionChange);
    };
  }, [player]);

  return {
    currentResolution,
    currentResolutionOption,
    supportedResolutions,
    onResolutionOptionClick,
    setCurrentResolutionOption,
    setCurrentResolution,
  };
};
